import { Button, Modal } from '@/components/UI';
import dayjs from 'dayjs';
import type { BundlePreviewResponse } from './types';
import { formatPct01Number, formatSignedPct } from './helpers';
import { RAW_RETURN_SUM_DESCRIPTION, RAW_RETURN_SUM_LABEL, WINSORIZED_RETURN_SUM_LABEL } from './returnMetricCopy';

interface BundlePreviewModalProps {
    data: BundlePreviewResponse | null;
    onClose: () => void;
    onCreateBundle: () => void;
}

const BundlePreviewModal = ({ data, onClose, onCreateBundle }: BundlePreviewModalProps) => {
    return (
        <Modal isOpen={data !== null} size="lg" title="Bundle Preview" onClose={onClose}>
            {data && (
                <>
                    <div className="text-zinc-400 text-sm mb-3">{data.ruleSummary}</div>

                    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 mb-3">
                        <div className="text-white text-sm font-semibold mb-2">In-sample</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-zinc-400">
                            <div>
                                Trades:{' '}
                                <span className="text-white">
                                    {data.metrics.inSample.passingCount.toLocaleString()}/
                                    {data.metrics.inSample.totalCount.toLocaleString()}
                                </span>
                            </div>
                            <div>
                                Coverage: <span className="text-white">{formatPct01Number(data.metrics.inSample.coverage, 1)}</span>
                            </div>
                            <div>
                                Win rate: <span className="text-white">{formatPct01Number(data.metrics.inSample.winRate, 1)}</span>
                            </div>
                            <div>
                                {RAW_RETURN_SUM_LABEL}:{' '}
                                <span className={data.metrics.inSample.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}>
                                    {formatSignedPct(data.metrics.inSample.totalProfit, 2)}
                                </span>
                                <div className="text-zinc-500 text-[10px]">
                                    expected:{' '}
                                    {formatSignedPct(data.metrics.inSample.baselineAvgProfit * data.metrics.inSample.passingCount, 2)}
                                </div>
                            </div>
                            <div>
                                Avg trade:{' '}
                                <span className={data.metrics.inSample.avgProfit >= 0 ? 'text-green-400' : 'text-red-400'}>
                                    {formatSignedPct(data.metrics.inSample.avgProfit, 2)}
                                </span>
                            </div>
                            <div>
                                Lift:{' '}
                                <span className={data.metrics.inSample.lift >= 0 ? 'text-green-400' : 'text-red-400'}>
                                    {formatSignedPct(data.metrics.inSample.lift, 2)}
                                </span>
                            </div>
                            <div>
                                Profit factor:{' '}
                                <span className="text-white">
                                    {data.metrics.inSample.profitFactor !== null
                                        ? `${data.metrics.inSample.profitFactor.toFixed(2)}x`
                                        : '-'}
                                </span>
                            </div>
                        </div>

                        <div className="text-zinc-500 text-[11px] mt-2">
                            {RAW_RETURN_SUM_DESCRIPTION} Baseline avg trade: {formatSignedPct(data.metrics.inSample.baselineAvgProfit, 2)}
                        </div>
                    </div>

                    {data.metrics.holdout && (
                        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 mb-3">
                            <div className="text-white text-sm font-semibold mb-2">Holdout (winsorized)</div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-zinc-400">
                                <div>
                                    Trades:{' '}
                                    <span className="text-white">
                                        {data.metrics.holdout.passingCount.toLocaleString()}/
                                        {data.metrics.holdout.totalCount.toLocaleString()}
                                    </span>
                                </div>
                                <div>
                                    Coverage: <span className="text-white">{formatPct01Number(data.metrics.holdout.coverage, 1)}</span>
                                </div>
                                <div>
                                    Win rate: <span className="text-white">{formatPct01Number(data.metrics.holdout.winRate, 1)}</span>
                                </div>
                                <div>
                                    {WINSORIZED_RETURN_SUM_LABEL}:{' '}
                                    <span
                                        className={
                                            data.metrics.holdout.ruleMeanProfit * data.metrics.holdout.passingCount >= 0
                                                ? 'text-green-400'
                                                : 'text-red-400'
                                        }
                                    >
                                        {formatSignedPct(data.metrics.holdout.ruleMeanProfit * data.metrics.holdout.passingCount, 2)}
                                    </span>
                                    <div className="text-zinc-500 text-[10px]">
                                        expected:{' '}
                                        {formatSignedPct(data.metrics.holdout.baselineMeanProfit * data.metrics.holdout.passingCount, 2)}
                                    </div>
                                </div>
                                <div>
                                    Mean trade:{' '}
                                    <span className={data.metrics.holdout.ruleMeanProfit >= 0 ? 'text-green-400' : 'text-red-400'}>
                                        {formatSignedPct(data.metrics.holdout.ruleMeanProfit, 2)}
                                    </span>
                                </div>
                                <div>
                                    Lift:{' '}
                                    <span className={data.metrics.holdout.lift >= 0 ? 'text-green-400' : 'text-red-400'}>
                                        {formatSignedPct(data.metrics.holdout.lift, 2)}
                                    </span>
                                </div>
                                <div>
                                    Winsor cap:{' '}
                                    <span className="text-white">
                                        {data.metrics.holdout.cap !== null ? `${data.metrics.holdout.cap.toFixed(2)}%` : '-'}
                                    </span>
                                </div>
                            </div>

                            <div className="text-zinc-500 text-[11px] mt-2">
                                Holdout baseline mean: {formatSignedPct(data.metrics.holdout.baselineMeanProfit, 2)} • window{' '}
                                {dayjs.unix(data.metrics.holdout.startTimestamp).format('MMM D, YYYY')} →{' '}
                                {dayjs.unix(data.metrics.holdout.endTimestamp).format('MMM D, YYYY')}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2">
                        <Button variant="secondary" onClick={onClose}>
                            Close
                        </Button>
                        <Button disabled={data.metrics.inSample.passingCount < 2} variant="primary" onClick={onCreateBundle}>
                            Create OR bundle
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default BundlePreviewModal;
